/**
 * @fileoverview Inventory Zustand store
 *
 * Caches the active list of items, stock rows, alerts, and stats
 * for the inventory views. Mirrors the work-order-store shape.
 *
 * @author FacilityAI
 * @version 1.0.0
 */

import type {
	InventoryItem,
	InventoryLowStockAlertRow,
	InventoryStats,
	InventoryStockRow,
	InventoryTransactionRow,
} from "@/services/inventory-service";
import { create } from "zustand";

export interface InventoryItemFilters {
	search?: string;
	category?: string;
	preferredVendorId?: number;
	compatibleAssetTypeId?: number;
	page: number;
	limit: number;
	sortBy: "name" | "category" | "created_at" | "updated_at";
	sortOrder: "asc" | "desc";
}

export interface InventoryStockFilters {
	itemId?: number;
	locationId?: number;
	lowStockOnly: boolean;
	search?: string;
	page: number;
	limit: number;
}

interface InventoryState {
	// Catalog
	items: InventoryItem[];
	itemsTotal: number;
	itemsLoading: boolean;
	itemFilters: InventoryItemFilters;

	// Stock
	stock: InventoryStockRow[];
	stockTotal: number;
	stockLoading: boolean;
	stockFilters: InventoryStockFilters;

	// Activity
	transactions: InventoryTransactionRow[];
	transactionsTotal: number;
	transactionsLoading: boolean;

	// Alerts
	alerts: InventoryLowStockAlertRow[];
	alertsTotal: number;
	alertsLoading: boolean;

	// Stats
	stats: InventoryStats | null;
	statsLoading: boolean;

	// Actions
	setItems: (items: InventoryItem[], total: number) => void;
	setItemFilters: (partial: Partial<InventoryItemFilters>) => void;
	setItemsLoading: (loading: boolean) => void;

	setStock: (rows: InventoryStockRow[], total: number) => void;
	setStockFilters: (partial: Partial<InventoryStockFilters>) => void;
	setStockLoading: (loading: boolean) => void;

	setTransactions: (rows: InventoryTransactionRow[], total: number) => void;
	setTransactionsLoading: (loading: boolean) => void;

	setAlerts: (rows: InventoryLowStockAlertRow[], total: number) => void;
	setAlertsLoading: (loading: boolean) => void;

	setStats: (stats: InventoryStats | null) => void;
	setStatsLoading: (loading: boolean) => void;

	upsertItemInList: (item: InventoryItem) => void;
	removeItemFromList: (id: number) => void;
}

const DEFAULT_ITEM_FILTERS: InventoryItemFilters = {
	page: 1,
	limit: 50,
	sortBy: "name",
	sortOrder: "asc",
};

const DEFAULT_STOCK_FILTERS: InventoryStockFilters = {
	lowStockOnly: false,
	page: 1,
	limit: 100,
};

export const useInventoryStore = create<InventoryState>((set) => ({
	items: [],
	itemsTotal: 0,
	itemsLoading: false,
	itemFilters: { ...DEFAULT_ITEM_FILTERS },

	stock: [],
	stockTotal: 0,
	stockLoading: false,
	stockFilters: { ...DEFAULT_STOCK_FILTERS },

	transactions: [],
	transactionsTotal: 0,
	transactionsLoading: false,

	alerts: [],
	alertsTotal: 0,
	alertsLoading: false,

	stats: null,
	statsLoading: false,

	setItems: (items, total) => set({ items, itemsTotal: total }),
	setItemFilters: (partial) =>
		set((s) => ({ itemFilters: { ...s.itemFilters, ...partial } })),
	setItemsLoading: (loading) => set({ itemsLoading: loading }),

	setStock: (rows, total) => set({ stock: rows, stockTotal: total }),
	setStockFilters: (partial) =>
		set((s) => ({ stockFilters: { ...s.stockFilters, ...partial } })),
	setStockLoading: (loading) => set({ stockLoading: loading }),

	setTransactions: (rows, total) =>
		set({ transactions: rows, transactionsTotal: total }),
	setTransactionsLoading: (loading) => set({ transactionsLoading: loading }),

	setAlerts: (rows, total) => set({ alerts: rows, alertsTotal: total }),
	setAlertsLoading: (loading) => set({ alertsLoading: loading }),

	setStats: (stats) => set({ stats }),
	setStatsLoading: (loading) => set({ statsLoading: loading }),

	upsertItemInList: (item) =>
		set((s) => {
			const existing = s.items.findIndex((i) => i.id === item.id);
			if (existing >= 0) {
				const next = [...s.items];
				next[existing] = item;
				return { items: next };
			}
			return { items: [item, ...s.items], itemsTotal: s.itemsTotal + 1 };
		}),

	removeItemFromList: (id) =>
		set((s) => ({
			items: s.items.filter((i) => i.id !== id),
			itemsTotal: Math.max(0, s.itemsTotal - 1),
		})),
}));
